// nebula/looks.js: nebula configurations. 'ignite' is hand-composed (chapter 0);
// brandLook(seed) derives a unique but always on-palette nebula from a seed.
// All positions in world units; the volume box is BOX_MIN..BOX_MAX, star at the origin.
// Lighting model: an off-frame cluster (lDir) is the key light before ignition: it rim-lights
// the dust (albL), makes the cluster-facing ionisation fronts glow (cR) and the diffuse
// ionised veil glow (cG). The new star then lights everything from inside (alb, cR2, cH/cO/cS).
import { rng, tempToRGB } from '../lib/common.js';

export const BOX_MIN = [-9, -5, -6];
export const BOX_MAX = [9, 5, 6];

const norm = (v) => { const l = Math.hypot(v[0], v[1], v[2]) || 1; return [v[0] / l, v[1] / l, v[2] / l]; };
const mix3 = (a, b, t) => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
const mul3 = (a, k) => [a[0] * k, a[1] * k, a[2] * k];

// FNV-1a: brand name -> 32-bit seed (case/space-insensitive so "Nike" == " nike ")
export function hashBrand(s) {
  s = String(s || '').trim().toLowerCase();
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
  // extra avalanche so short names that differ by one letter land far apart
  h ^= h >>> 16; h = Math.imul(h, 0x7feb352d) >>> 0; h ^= h >>> 15; h = Math.imul(h, 0x846ca68b) >>> 0; h ^= h >>> 16;
  return h >>> 0;
}

// JWST-style palettes (linear rgb): amber/gold dust and fronts against violet-blue ionised
// gas, rose H-alpha, a little OIII. All sit inside the brand family.
// Round 6 colour pivot (web/src/worlds/LIGHT.md): JWST infrared, brand palettes spread over the hue wheel.
// Keys kept (the brand hash picks them by index); contents recoloured. Linear RGB, HDR multipliers.
const DUSTE = { cliffs: [0.016, 0.004, 0.002], ember: [0.005, 0.005, 0.016], violet: [0.014, 0.003, 0.012], aurora: [0.02, 0.002, 0.004], gold: [0.004, 0.014, 0.009] };
export const PALETTES = {
  // IGNITE, JWST NIRCam Carina: navy sky, teal gas, turquoise OIII, copper-rust dust, magenta ionisation fronts
  cliffs: { lCol: [0.6, 0.82, 1.0], albL: [1.3, 0.43, 0.17], alb: [1.3, 0.47, 0.2], cR: [0.9, 0.06, 0.42], cR2: [0.85, 0.14, 0.45], cG: [0.01, 0.48, 0.5], cH: [1.0, 0.08, 0.48], cO: [0.12, 0.68, 0.56], cS: [0.75, 0.03, 0.3], amb: [0.002, 0.006, 0.014], bgA: [0.012, 0.035, 0.16], bgB: [0.01, 0.17, 0.22] },
  // indigo-blue, magenta accents
  ember: { lCol: [0.6, 0.7, 1.0], albL: [0.42, 0.45, 1.0], alb: [0.45, 0.5, 1.0], cR: [0.75, 0.25, 1.3], cR2: [0.85, 0.35, 1.4], cG: [0.35, 0.12, 1.0], cH: [0.55, 0.22, 1.2], cO: [0.4, 0.75, 1.1], cS: [0.9, 0.1, 0.55], amb: [0.003, 0.004, 0.014], bgA: [0.03, 0.05, 0.3], bgB: [0.03, 0.08, 0.5] },
  // hot pink / magenta, cyan accents
  violet: { lCol: [0.9, 0.6, 1.0], albL: [0.9, 0.25, 0.75], alb: [1.0, 0.28, 0.8], cR: [1.3, 0.15, 0.9], cR2: [1.4, 0.3, 1.0], cG: [0.7, 0.1, 0.9], cH: [1.2, 0.1, 0.7], cO: [0.2, 0.8, 1.0], cS: [0.6, 0.03, 0.45], amb: [0.008, 0.003, 0.012], bgA: [0.2, 0.03, 0.25], bgB: [0.08, 0.05, 0.4] },
  // crimson red, violet accents
  aurora: { lCol: [1.0, 0.75, 0.85], albL: [1.1, 0.14, 0.2], alb: [1.2, 0.16, 0.22], cR: [1.4, 0.1, 0.2], cR2: [1.5, 0.2, 0.3], cG: [0.9, 0.05, 0.25], cH: [1.1, 0.06, 0.14], cO: [0.55, 0.3, 1.0], cS: [0.7, 0.02, 0.12], amb: [0.012, 0.002, 0.005], bgA: [0.2, 0.02, 0.1], bgB: [0.12, 0.03, 0.35] },
  // emerald / spring green, azure accents
  gold: { lCol: [0.7, 1.0, 0.9], albL: [0.25, 0.8, 0.5], alb: [0.28, 0.9, 0.55], cR: [0.35, 1.2, 0.18], cR2: [0.45, 1.3, 0.3], cG: [0.08, 0.3, 0.95], cH: [0.08, 1.0, 0.5], cO: [0.5, 0.3, 1.1], cS: [0.05, 0.5, 0.35], amb: [0.002, 0.008, 0.007], bgA: [0.05, 0.04, 0.25], bgB: [0.03, 0.08, 0.4] },
};

function base() {
  return {
    seed: 3.1,
    star: [0, 0, 0],
    axis: norm([0.25, 0.9, 0.36]),     // tilted ~20 deg toward the camera: spiral and hourglass both read
    warp: [0.55, 0.32, 0.22, 0.9],
    wall: null, pillars: [], globs: [], shell: null, lobe: null, ring: null, cliff: null,
    lanes: [0, 0.3, 2.8, 5.0],
    gas: [0.3, 0.5, 2.6, 1.2],         // star-lit emission gas: amount, frequency, radius, filaments
    front: [0.75, 0.32, 1.0, 0.45],    // ionised veil: amount, frequency, cluster-side bias, dust clearance
    str: [0.8, 1.0, 2.6, 0],           // march striations: erosion, shading mix, frequency, radial mode (ring radius)
    med: [1.0, 0.56, 0.26, 7.5],       // dust scale, erosion, detail frequency, dust extinction
    gasP: [1.0, 0.08, 0.9, 0],         // gas scale, gas extinction, filament contrast
    redden: [0.8, 1.0, 1.25],
    gain: [0.9, 0.15, 0.75, 1.0],       // star-gas emission, star-lit dust glow, star scattering, ionisation
    rim: [32.0, 6.5, 0.2, 0],         // cluster-front glow, star-front glow, veil glow
    rimLit: 1.0,                       // cluster-front glow multiplier once the star is lit
    lDir: norm([0.12, 0.6, -0.79]),    // towards the ionising cluster: above and behind, so ridges are backlit
    lI: 0.3, lIlit: 0.7,              // cluster light before / multiplier after ignition
    extL: 1.0,                         // cluster light extinction scale
    frontGas: 1.0, clusterGas: 1.0, tapK: 0.9, cavR: 1.25, swLit: 0.7, veilStar: 0.5,
    starT: 7200, starL: 1.0,
    bgI: 0.4,
    hz: [0.0, 0.0, 0.25, 0],            // horizon glow above a cliff line: amount, ray y, falloff
    shellCol: [1.0, 0.55, 0.45],
    expo: 1.0,
    fill: { dir: norm([0.0, 0.8, 0.6]), i: 0.35, y0: -5.0, y1: 0.8, c: [1.0, 0.42, 0.16] },   // UV-excited dust glow from above-front
    pal: 'cliffs',
    kind: 'ignite',
    cam: { d: 9.0, az: -0.14, el: 0.1, ty: -0.35, roll: 0.0 },
  };
}

// Chapter 0: Cosmic-Cliffs style. A ragged dust ridge fills the lower frame with pillars
// rising out of it, the ionised veil above, foreground wisps. The protostar forms in a
// dense knot just above the ridge, at the tip of the central pillar.
export function igniteLook() {
  const L = base();
  // a dust wall facing the camera (face ~0.7 behind the star), ragged top edge at the star's height
  L.cliff = { top: -0.25, amp: 1.35, freq: 0.3, face: -0.7, faceAmp: 0.9, faceFreq: 0.35, foot: 1.1 };
  L.pillars = [
    [-3.0, -3.0, -1.6, 1.05, -2.5, 1.9, -1.9, 0.42],
    [3.3, -3.0, -2.2, 0.95, 3.7, 1.4, -2.5, 0.38],
    [-5.6, -3.0, -2.8, 1.2, -6.0, 2.4, -3.2, 0.55],
    [0.3, -3.0, -0.9, 0.55, 0.0, -0.1, 0.0, 0.3],
  ];
  L.globs = [[0.0, 0.0, 0.0, 0.6], [0.4, -0.5, -0.3, 0.55]];
  L.lanes = [0.4, 0.3, 3.0, 5.4];
  L.hz = [1.0, 0.0, 0.2, 0];
  L.clusterGas = 3.0;
  L.eroMix = 0.7;   // base erosion leans ridged: filaments, fewer cotton billows
  L.starT = 13000;  // blue-white newborn star
  // r7: the star's blown ionised cavity (uGasF.z, live since r7): closed-form teal core + magenta walls;
  // the wall dust near the star scatters magenta-white instead of copper-tan (no brown veil)
  L.cavR = 1.5; L.cavOpen = 0.8; L.cavGlow = 0.112; L.cavWall = 1.0; L.albC = [0.85, 0.1, 0.5, 1.0];
  L.gxK = 0.9; L.dep = [1.0, 0.85, 0.3];   // bulk gas/glow off; gas at the fronts (frW); bulk diffuse dim   // r7 depth after ignition (uDep): bulk gas/glow off, low-ion gas dim, bulk diffuse dim
  L.cam = { d: 9.0, az: -0.12, el: 0.02, ty: 0.25, roll: 0.0 };
  // r8 (JWST NIRCam Cosmic Cliffs): once lit, dark rust/umber dust bodies, magenta only in thin star-facing skins and
  // full-res rims, teal-cyan veils over the cliff tops. world.js blends it in with the blow-out (k_r8 = 0 = r7).
  L.r8 = { alb: [1.1, 0.42, 0.2], veil: 0.35, veilC: [0.03, 0.4, 0.52], veilY: [-1.9, -0.7, 1.3, 2.8], tapK: 1.25, depZ: 0.55, cw: 0.5, nsc: 0.3,
    fillC: [1.0, 0.36, 0.16], fillI: 2.6, fillIp: 2.2, dE: 3.5, push: { sk: 1.0, cw: 3.0, cg: 1.0, vl: 1.0, ht: 0.85 }, haloC: [0.04, 1.2, 1.05], cO: [0.03, 0.5, 0.74], ct: 0.5, footD: 3.5, gd: 0.9, albC: [1.0, 0.3, 0.16], rimM: [1.0, 0.1, 0.55], rimG: 2.0, stepL: 0.8, sh: [0.0, 0.018, 0.045] };
  // r9 (R9 lit programs, k_r9 = 0 = r8): pre-integrated skins (skG gain), lit rust ridges (fill / star scatter / dust glow, phones most),
  // crisp magenta rims at full res (rimB gain, flat pink -> umber umb, tap tapT texels, off within rimR px of the star)
  L.r9 = { skG: 1.0, fillD: 1.2, gzD: 1.1, fillP: 2.0, gzP: 1.6, dEP: 1.6, expoP: 1.1, rimB: 5.0, flat: 0.6, dome: 0.75, edgeG: 1.6, domeR: 1.4, satP: 1.18, htP: 0.8, stepL9: 0.85, tapT: 1.5, rimR: 110, umb: [1.859, 0.743, 0.353] };
  return finish(L);
}

// Brand nebula from a 32-bit seed: archetype, geometry, palette, light and star temperature.
// archetype + palette from their own hash bits (salted so the showcase names Starlex/Nike/Zomato/Tesla land on 4 different shapes)
const KINDS = ['pillars', 'cliffs', 'bubble', 'bipolar', 'ring'], PAL_KEYS = ['cliffs', 'ember', 'violet', 'aurora', 'gold'];
const KSALT = 63, PSALT = 24;
export function brandLook(seed) {
  const r = rng((seed >>> 0) || 1);
  const R = (a, b) => a + (b - a) * r();
  const pick = (arr) => arr[Math.floor(r() * arr.length) % arr.length];
  const L = base();
  r(); L.kind = KINDS[(Math.imul((seed ^ KSALT) >>> 0, 0x9e3779b1) >>> 0) % 5];
  L.seed = R(0, 97);
  r(); L.pal = PAL_KEYS[(Math.imul((seed ^ PSALT) >>> 0, 0x85ebca6b) >>> 0) % 5];
  L.warp = [R(0.4, 0.75), R(0.25, 0.42), R(0.12, 0.3), R(0.7, 1.2)];
  L.axis = norm([R(-0.5, 0.5), R(0.6, 1), R(-0.3, 0.5)]);
  L.lDir = norm([R(-0.6, 0.6), R(0.55, 1.0), R(-0.6, 0.0)]);
  L.starT = R(5200, 14000);
  L.gain = [1.15, 0.12, 0.7, 1.0];   // brand: an emission nebula (gas glow) more than lit dust
  L.gas = [R(0.55, 0.8), R(0.35, 0.6), R(1.7, 2.5), R(0.8, 1.3)];   // shaped: emission hugs the newborn star
  L.front = [R(0.35, 0.6), R(0.25, 0.42), 1.0, R(0.35, 0.6)];
  L.bgI = R(0.22, 0.32);
  L.med[1] = R(0.5, 0.7);
  L.rim[0] = R(12, 20);
  const side = r() < 0.5 ? -1 : 1;
  if (L.kind === 'pillars') {
    L.wall = { c: [R(-0.5, 0.5), R(0.4, 1.2), R(-3.6, -2.6)], r: R(4.6, 5.4), noise: R(0.2, 0.3), cover: R(0.1, 0.3), open: norm([R(-0.2, 0.2), 0.1, 1]), holes: R(0.5, 0.8), thick: 0.5 };
    const n = 2 + Math.floor(r() * 3);
    for (let i = 0; i < n; i++) {
      const x = side * R(0.8, 4.0) * (i % 2 ? -0.7 : 1), z = R(-2.4, 0.4), h = R(-0.6, 1.6);
      L.pillars.push([x * 1.1, -5.3, z, R(0.55, 0.95), x * 0.9 + R(-0.4, 0.4), h, z * 0.9, R(0.24, 0.42)]);
    }
    for (let i = 0; i < 3; i++) L.globs.push([R(-2.6, 2.6), R(-0.8, 1.8), R(-1.5, 1.2), R(0.14, 0.3)]);
    L.cliff = r() < 0.5 ? { top: R(-3.6, -3.0), amp: R(0.6, 1.0), freq: R(0.3, 0.45) } : null;
  } else if (L.kind === 'cliffs') {
    L.cliff = { top: R(-2.0, -1.2), amp: R(0.9, 1.4), freq: R(0.28, 0.44) };
    L.hz = [R(0.6, 1.0), R(-0.15, 0.0), R(0.18, 0.3), 0];
    L.wall = { c: [0, 1.0, -3.4], r: R(4.8, 5.6), noise: 0.26, cover: R(0.1, 0.3), open: norm([0, 0.1, 1]), holes: 0.6, thick: 0.5 };
    const n = Math.floor(r() * 3);
    for (let i = 0; i < n; i++) { const x = R(-4, 4), z = R(-2.2, 0.2); L.pillars.push([x, -3.4, z, R(0.5, 0.8), x + R(-0.6, 0.6), R(-0.8, 1.0), z, R(0.22, 0.36)]); }
    for (let i = 0; i < 2; i++) L.globs.push([R(-2.5, 2.5), R(0.3, 2), R(-1.2, 1.0), R(0.14, 0.26)]);
  } else if (L.kind === 'bubble') {
    L.shell = { c: [R(-0.3, 0.3), R(-0.3, 0.3), R(-0.3, 0.3)], r: R(1.7, 2.3), thick: R(0.2, 0.34), frag: R(0.4, 0.62) };
    L.wall = { c: [0, 0, -1.6], r: R(4.4, 5.2), noise: 0.25, cover: R(0.1, 0.4), open: norm([0, 0.2, 1]), holes: 0.6, thick: 0.5 };
    for (let i = 0; i < 2; i++) L.globs.push([R(-2.4, 2.4), R(-1.8, 1.8), R(-1.0, 1.0), R(0.12, 0.26)]);
    L.coreK = 1.6;
    L.gas[2] = R(2.2, 3.0);
    L.lDir = norm([R(-0.4, 0.4), R(0.2, 0.6), -1]);
  } else if (L.kind === 'bipolar') {
    L.lobe = { len: R(2.6, 3.6), width: R(0.9, 1.5), waist: R(0.4, 0.8), ax: norm([R(-0.6, 0.6), 1, R(-0.3, 0.3)]) };
    L.axis = L.lobe.ax;
    L.wall = { c: [0, 0, -2.4], r: 5.4, noise: 0.3, cover: 0.3, open: norm([0, 0, 1]), holes: 0.7, thick: 0.6 };
    L.gas[0] *= 0.8;
  } else {
    L.ring = { r: R(1.4, 2.0), tube: R(0.24, 0.38), clump: R(0.4, 0.9), n: norm([R(-0.5, 0.5), R(-0.4, 0.4), 1]) };
    L.str = [0, 0.9, 6.0, L.ring.r]; L.med[1] = 0.3; L.gasP = [1.0, 0.08, 1.0, 0];   // strands: the march's detail field streams radially
    L.starT = R(9000, 16000);
    L.gas = [0.35, 0.5, 2.2, 0.4];
    for (let i = 0; i < 3; i++) L.globs.push([R(-3, 3), R(-2, 2), R(-2.5, -0.5), R(0.12, 0.24)]);
    L.wall = null;   // M57 hangs in empty space (the lit wall read as a crimson backdrop; also cheaper)
  }
  L.lanes = [R(0.35, 0.8), R(0.25, 0.4), 2.8, 5.2];
  L.cam = { d: R(7.0, 8.6), az: R(-0.45, 0.45), el: R(-0.02, 0.2), ty: R(-0.35, 0.1), roll: R(-0.07, 0.07) };
  L.palJit = { g: R(0.85, 1.15), r: R(0.9, 1.1), h: R(0.85, 1.15) };
  return finish(L);
}

function finish(L) {
  const P = PALETTES[L.pal];
  const j = L.palJit || { g: 1, r: 1, h: 1 };
  Object.assign(L, P);
  L.dustE = DUSTE[L.pal];
  L.cR2 = P.cR2.slice();
  { const m = Math.max(...P.albL); L.fill = Object.assign({}, L.fill, { c: P.albL.map(v => v / m) }); }
  L.shellCol = mix3(P.cH, P.cO, 0.35);
  L.cG = mul3(P.cG, j.g); L.cR = mul3(P.cR, j.r); L.cH = mul3(P.cH, j.h);
  // star colour: blackbody pulled toward the brand white-gold so it never reads cold-blue or orange
  const bb = tempToRGB(L.starT);
  L.starCol = mix3(bb, [0.84, 0.92, 1.0], 0.55);   // blue-white (LIGHT.md)
  L.coreCol = mix3(bb, [0.88, 0.94, 1.0], 0.5);
  if (L.ring) {   // Starlex = JWST Ring Nebula (M57): cyan-teal core #5FE3E0, violet filament shell #8A5CFF, crimson outer knots #FF3B6B
    L.cO = [0.12, 0.8, 0.78]; L.cH = [0.3, 0.12, 1.1]; L.cS = [1.1, 0.05, 0.16]; L.cG = [0.3, 0.14, 1.0];
    L.alb = [0.8, 0.1, 0.35]; L.albL = [0.6, 0.12, 0.5]; L.cR = [1.1, 0.05, 0.2]; L.cR2 = [1.0, 0.1, 0.4];
    L.lCol = [0.7, 0.65, 1.0]; L.bgA = [0.05, 0.02, 0.2]; L.bgB = [0.03, 0.07, 0.32]; L.dustE = [0.01, 0.002, 0.008];
    L.amb = [0.003, 0.002, 0.01]; L.shellCol = mix3(L.cH, L.cO, 0.4); L.fill.c = [0.6, 0.2, 1.0]; L.rhz = 0.15;
    L.gas = [L.gas[0], L.gas[1], 1.2, L.gas[3]];   // diffuse gas stays near the star: dark sky outside the ring
  }
  return L;
}
